import { mdiFileDownloadOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Input,
  Table,
  Spinner,
  Button,
} from 'reactstrap';
import './ReassignApprovals.scss';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import ReassignApprovalForm from '../../../components/Admin/ReassignApprovalForm/ReassignApprovalForm';
import { tableColumns } from './constants';
import {
  fetchReassignApprovals,
  fetchRequestTypeList,
} from '../../../store/apis/reassignApprovalService';
import { useTableHook } from '../../../components/hooks';
import { fetchTableColumnValues } from '../../../store/apis/commonService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TablePagination,
  TableSortDisplayArrow,
  UserAsyncAutocompleteSelect,
  CustomSelect,
} from '../../../components/Common';
import { ExportUtil } from '../../../utils';
import { fetchDelegateApprovalAutoComplete } from '../../../store/apis/autocompleteService';
import { Messages } from '../../../common/messages';

function ReassignApprovals() {
  const { data: requestTypeOptions = [] } = useQuery({
    queryKey: ['requestTypeList'],
    queryFn: fetchRequestTypeList,
  });

  const [reassignApprovalOC, setReassignApprovalOC] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const initialDefaultExternalFilters = {
    approvalType: '',
    requestorID: '',
    primaryApprover: '',
    secondaryApprover: '',
  };

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    nextPage,
    prevPage,
    handleSetExternalFilters,
    applyExternalFilters,
    setPageLoadTrigger,
    tableDataMutation,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    isLoadingData,
    isLoadingColumn,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      ...initialDefaultExternalFilters,
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'RequestId',
      sortDirection: 'ASC',
      filters: [],
    },
    fetchFn: fetchReassignApprovals,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'ReassignApprovals',
    columnKeyPrefix: 'ReassignApprovalsColumn',
    tableName: 'ReassignApprovals',
    defaultExternalFilters: initialDefaultExternalFilters,
    onPageLoadTrigger: false,
  });

  useEffect(() => {
    setPageLoadTrigger(true);
    setPageRequest(prev => ({
      ...prev,
    }));
  }, [setPageRequest, setPageLoadTrigger]);

  const toggleReassignApprovalOC = () => {
    setReassignApprovalOC(!reassignApprovalOC);
  };

  const handleCheckboxChange = (e, id) => {
    if (e.target.checked) {
      setSelectedRequests(prev => [...prev, id]);
    } else {
      setSelectedRequests(prev => prev.filter(item => item !== id));
    }
  };

  const handleSelectAll = e => {
    const isChecked = e.target.checked;
    const tableData = data?.data;
    setSelectAll(isChecked);
    setSelectedRequests(
      isChecked ? tableData?.map(item => item.RequestId) : [],
    );
  };

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    const exportData = data?.data;
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    ExportUtil.downloadExcel(exportResponseData?.data, {
      fileName: `Reassign_Approvals_${timesTamp}.xlsx`,
      columns: tableColumns,
    });
  };

  const renderTableBody = () => {
    const tableData = data?.data;
    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={8}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_REASSIGN
            )}
          </td>
        </tr>
      );
    }
    return tableData?.map((item, index) => (
      <tr key={index}>
        <td>
          <Input
            type="checkbox"
            className="blue-checkbox"
            onChange={e => handleCheckboxChange(e, item.RequestId)}
            checked={selectedRequests.includes(item.RequestId)}
          />
        </td>
        <td>{item.RequestId}</td>
        <td>{item.ApprovalTyp}</td>
        <td>{item.RequestDate}</td>
        <td title={item.Requester}>{item.Requester}</td>
        <td title={item.RequestDetails}>{item.RequestDetails}</td>
        <td>{item['Primary Approver']}</td>
        <td>{item['Secondary Approver']}</td>
      </tr>
    ));
  };

  const getSelectedRowsData = () => {
    const tableData = data?.data;
    return tableData?.filter(item => selectedRequests.includes(item.RequestId));
  };

  useEffect(() => {
    setSelectedRequests([]);
    setSelectAll(false);
  }, [pageRequest]);

  return (
    <div className="page-content ra-page">
      <Card className="ra-card">
        <CardHeader
          className="d-flex justify-content-between align-items-center pt-2 pb-2 border-0"
          style={{ borderRadius: '10px 10px 0 0' }}
        >
          <div>
            <span className="fs-5" style={{ color: '#000', fontWeight: '600' }}>
              Reassign Approvals
            </span>
          </div>
          <div className="d-flex flex-row align-items-center gap-2">
            <Button
              type="button"
              className="custom-light-btn"
              onClick={handleExportExcel}
            >
              <span>Download as Excel</span>
              <Icon path={mdiFileDownloadOutline} size={0.7} />
            </Button>
            <Button
              type="button"
              className="custom-btn"
              onClick={toggleReassignApprovalOC}
              disabled={selectedRequests.length === 0}
            >
              Reassign
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="external-filter gap-2 mx-3 p-3">
            <Col className="external-filter-col">
              <span className="fw-semibold">Request Type</span>
              <CustomSelect
                options={requestTypeOptions?.map(type => ({
                  value: type.Description,
                  label: type.Description,
                }))}
                onChange={e => handleSetExternalFilters('approvalType', e)}
                placeholder="Select Request Type"
                className="external-filter-input"
                value={externalFilters.approvalType}
              />
            </Col>
            <Col className="external-filter-col">
              <span className="fw-semibold">Requester</span>
              <UserAsyncAutocompleteSelect
                autoCompleteFn={input =>
                  fetchDelegateApprovalAutoComplete({
                    prefixText: input,
                  })
                }
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.ID,
                    label: item.Name,
                    name: item.Name,
                    code: item.Cd,
                    designation: item.Designation,
                    image: item?.Imagefile,
                    coCd: item?.CoCd?.trim(),
                  }))
                }
                onChange={selectedOption =>
                  handleSetExternalFilters('requestorID', selectedOption?.value)
                }
                placeholder="Requester"
                isClearable
                isSearchable
              />
            </Col>
            <Col className="external-filter-col">
              <span className="fw-semibold">Primary Approver</span>
              <UserAsyncAutocompleteSelect
                autoCompleteFn={input =>
                  fetchDelegateApprovalAutoComplete({
                    prefixText: input,
                  })
                }
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.ID,
                    label: item.Name,
                    name: item.Name,
                    code: item.Cd,
                    designation: item.Designation,
                    image: item?.Imagefile,
                    coCd: item?.CoCd?.trim(),
                  }))
                }
                onChange={selectedOption =>
                  handleSetExternalFilters(
                    'primaryApprover',
                    selectedOption?.value,
                  )
                }
                placeholder="Primary Approver"
                isClearable
                isSearchable
              />
            </Col>
            <Col className="external-filter-col">
              <span className="fw-semibold">Secondary Approver</span>
              <UserAsyncAutocompleteSelect
                autoCompleteFn={input =>
                  fetchDelegateApprovalAutoComplete({
                    prefixText: input,
                  })
                }
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.ID,
                    label: item.Name,
                    name: item.Name,
                    code: item.Cd,
                    designation: item.Designation,
                    image: item?.Imagefile,
                    coCd: item?.CoCd?.trim(),
                  }))
                }
                onChange={selectedOption =>
                  handleSetExternalFilters(
                    'secondaryApprover',
                    selectedOption?.value,
                  )
                }
                placeholder="Secondary Approver"
                isClearable
                isSearchable
              />
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <div className="external-filter-btns gap-2">
                <button
                  type="button"
                  className="custom-btn"
                  onClick={() => applyExternalFilters(externalFilters)}
                >
                  Search
                </button>
              </div>
            </Col>
          </div>
          <div className="my-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={tableColumns}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive reassign-approval-table-container">
            <Table
              hover
              className="custom-table align-middle text-nowrap reassign-approval-table"
              id="reassign-approval"
            >
              <thead>
                <tr>
                  <th className="px-2">
                    <Input
                      type="checkbox"
                      className="blue-checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  {Object.entries(tableColumns).map(([key, value]) => (
                    <th key={key} className="px-2">
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => handleAutoSort(key)}
                        >
                          {value}
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <TableSortDisplayArrow
                            direction={checkColumnIfSorted(key)}
                          />
                          <FilterDropdown
                            handleSetColumnName={() => {
                              setColumnName(key);
                            }}
                            setColumnName={setColumnName}
                            columnData={columnData}
                            applyFilter={applyFilter}
                            columnName={columnName}
                            pageRequest={pageRequest}
                            setPageRequest={setPageRequest}
                            handleManualSort={handleManualSort}
                            isLoading={isLoadingColumn}
                          />
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2" style={{ borderRadius: '0 0 10px 10px' }}>
          <div>
            <TablePagination
              nextPage={nextPage}
              prevPage={prevPage}
              totalPages={data?.totalPages}
              currentPage={data?.currentPage}
              hasPreviousPage={data?.hasPreviousPage}
              hasNextPage={data?.hasNextPage}
              getPageSize={getPageSize}
              setPageSize={setPageSize}
              totalCount={data?.totalCount}
            />
          </div>
        </CardFooter>
      </Card>
      <ReassignApprovalForm
        isOpen={reassignApprovalOC}
        toggle={toggleReassignApprovalOC}
        selectedData={getSelectedRowsData()}
        closeModal={() => {
          setReassignApprovalOC(false);
          setSelectedRequests([]);
        }}
      />
    </div>
  );
}

export default ReassignApprovals;
